import React, { useEffect, useState } from 'react';
import { Mail, AlertCircle } from 'lucide-react';
import { apiService } from '../../services/api';

interface EmailVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultEmail?: string;
  onResendSuccess?: (_message: string) => void;
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EmailVerificationModal: React.FC<EmailVerificationModalProps> = ({
  isOpen,
  onClose,
  defaultEmail,
  onResendSuccess,
}) => {
  const initialEmail = defaultEmail && emailRegex.test(defaultEmail) ? defaultEmail : '';
  const [email, setEmail] = useState(initialEmail);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setEmail(defaultEmail && emailRegex.test(defaultEmail) ? defaultEmail : '');
      setError('');
      setIsSending(false);
    }
  }, [isOpen, defaultEmail]);

  if (!isOpen) return null;

  const handleResend = async () => {
    setError('');

    const trimmed = email.trim();
    if (!trimmed) {
      setError('Please enter your email address.');
      return;
    }
    if (!emailRegex.test(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }

    setIsSending(true);
    try {
      const response = await apiService.resendEmailVerification(trimmed);
      if (response.success) {
        const successMessage =
          response.message ||
          `A new verification link has been sent to ${trimmed}. Please check your inbox.`;
        onClose();
        onResendSuccess?.(successMessage);
      } else {
        setError(response.error || 'Unable to resend verification email. Please try again.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to resend verification email. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-[var(--modal-overlay-background)] bg-opacity-50 flex items-center justify-center z-[1001] p-4 zoomOutLevel">
      <div className="bg-[var(--modal-background)] rounded-lg shadow-xl max-w-md w-full mx-4 animate-scale-in">
        <div className="px-6 pt-6 pb-5 text-center">
          {/* Mail icon in SmartEco green */}
          <div className="mx-auto mb-4 w-20 h-20 rounded-full flex items-center justify-center bg-gradient-to-b from-[#0F796B] to-[#6FDC95]">
            <Mail className="h-10 w-10 text-white" />
          </div>

          <h3 className="sm:text-2xl text-xl font-normal text-[var(--color-text)] mb-2">
            Verify your email to continue
          </h3>

          <p className="text-[var(--color-sub-title-text)] font-normal sm:text-sm text-xs mb-4">
            {initialEmail ? (
              <>
                We&apos;ve sent a verification link to{' '}
                <span className="font-medium text-[var(--color-text)] break-all">
                  {initialEmail}
                </span>
                . Please check your inbox and click the link to activate your account.
              </>
            ) : (
              <>
                Please enter your registered email below and we&apos;ll send a new
                verification link. Click the link in the email to activate your account.
              </>
            )}
          </p>

          <p className="sm:text-sm text-xs font-light text-[var(--color-sub-title-text)] mb-4">
            Please also check your spam or promotions folder just in case.
          </p>

          {/* Email input — editable so the user can fix typos */}
          <div className="text-left mb-2">
            <label htmlFor="verifyEmail" className="form-label text-sm">
              Email
            </label>
            <input
              id="verifyEmail"
              type="email"
              value={email}
              onChange={e => {
                setEmail(e.target.value);
                setError('');
              }}
              placeholder="your@email.com"
              className={`form-input dark:bg-[#F5F5F533] bg-[#F5F5F5] w-full mt-1 ${
                error ? 'border-red-500' : ''
              }`}
              disabled={isSending}
              autoComplete="email"
            />
            {error && (
              <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {error}
              </p>
            )}
          </div>

          {/* Actions — Close is the primary; resend is a subtler text link so
              the user isn't nudged to re-spam the resend endpoint on every open. */}
          <div className="flex flex-col gap-3 mt-4">
            <div className="text-center">
              <button
                type="button"
                onClick={handleResend}
                disabled={isSending}
                className="text-sm font-medium text-[#0F796B] underline underline-offset-2 hover:opacity-75 disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
              >
                {isSending ? 'Sending a new link…' : 'Didn’t receive the email? Resend it'}
              </button>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="add-device-save-btn py-3"
            >
              Close
            </button>
          </div>

          <p className="text-xs font-light text-[var(--color-sub-title-text)] mt-4">
            Still having trouble? Contact us at{' '}
            <a
              href="mailto:smartecosupport@ispatialtec.com"
              className="underline text-[#0F796B] hover:opacity-75"
            >
              smartecosupport@ispatialtec.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationModal;
